import React, { useEffect, useState, useMemo } from "react";
import { Stack, Dropdown, IDropdownOption, Button, IDropdownStyles } from "@fluentui/react";
import { ggroupcardprops, ggroupcardslistprops } from "./../../interfaces";
import styles from "./GroupCardsList.module.css";
import DocumentCardComponent from "./DocumentCardComponent";
import CreateNewSpaceCard from "./CreateNewSpaceCard";
import ShimmerDocumentCard from "./ShimmerDocumentCard";
import { assitantCardsDropdownStyles, assitantCardsDropdownWiderStyles, assitantCardsDropdownThinStyles, groupStackDnb } from "./../../styles_glob";
import { isTouchDevice } from "./../../util_glob";
import { nmeMap, filterOptions, sortingOptions, localStorageSpaceSortingProp, localStorageSpaceFilterProp, localStorageManualOrderProp, localStorageHiddenGroupsProp } from "./../../lsts";


// Import @dnd-kit components
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragOverlay } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, rectSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import AnimAttention from "../AnimBut/AnimAttention";

const GroupCardsList: React.FC<ggroupcardslistprops> = ({ grL, onNewGroupClick, compLvl, permissionLevelsMap, newSpacePyro, navChatLib }) => {
    const [refreshKey, setRefreshKey] = useState(0);
    const [displayKey, setDisplayKey] = useState(0);
    const [grLState, setGrLState] = useState(grL);

    const [compLvlState, setCompLvlState] = useState<number>(compLvl);
    const [permissionLevelsMapState, setPermissionLevelsMapState] = useState(permissionLevelsMap);

    const [onlyMySpaces, setOnlyMySpaces] = useState(false);
    const [grLMainState, setGrLMainState] = useState<ggroupcardprops[]>([]);
    const [grLHiddenState, setGrLHiddenState] = useState<ggroupcardprops[]>([]);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [activeItem, setActiveItem] = useState<ggroupcardprops | null>(null);
    const [sortingProperty, setSortingProperty] = useState<string>(localStorage.getItem(localStorageSpaceSortingProp) || "oid");
    const [filteringProperty, setFilteringProperty] = useState<string>("all");
    const [assistantTypeFilter, setAssistantTypeFilter] = useState<string>(localStorage.getItem(localStorageSpaceFilterProp) || "all");
    const [manualOrder, setManualOrder] = useState<string[]>(localStorage.getItem(localStorageManualOrderProp)?.split(",") || []);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {distance: 30}
        })
    );
    const mobileSensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {delay: 1000, tolerance: 0}
        })
    );

    useEffect(() => {
        if (assistantTypeFilter != 'all' && grL?.filter(ff => ff.selectedGroup.assistanttype == assistantTypeFilter).length < 1 && grL?.length > 0) {
            localStorage.setItem(localStorageSpaceFilterProp, 'all');
            setAssistantTypeFilter('all');
            setTimeout(() => {
                setGrLState(grL);
                if (manualOrder.length != grL.length) {
                    let grLStrArr = grL.map(item => item.selectedGroup.selectionId);
                    if (grL.length > manualOrder.length) {
                        // grL has more strings, add them to the beginning of manualOrder
                        const extraItems = grLStrArr.filter(item => !manualOrder.includes(item));
                        localStorage.setItem(localStorageManualOrderProp, [...extraItems, ...manualOrder].join(","));
                        setManualOrder([...extraItems, ...manualOrder]);
                    } else {
                        // manualOrder has more strings, remove extras from grL
                        localStorage.setItem(localStorageManualOrderProp, manualOrder.filter(item => grLStrArr.includes(item))?.join(","));
                        setManualOrder(manualOrder.filter(item => grLStrArr.includes(item)));
                    }
                }
            }, 2500);
            return
        }
        if (compLvlState < 1 || !permissionLevelsMapState && grLState?.length == 0) return;

        // console.log("‹grl›", grL?.length, assistantTypeFilter);

        if (hasArrayChanged(grL)) {
            // empty
        } else {
            if (grL.length > 0) return;
        }
        setGrLState(grL);
        if (manualOrder.length != grL.length) {
            let grLStrArr = grL.map(item => item.selectedGroup.selectionId);
            if (grL.length > manualOrder.length) {
                // grL has more strings, add them to the beginning of manualOrder
                const extraItems = grLStrArr.filter(item => !manualOrder.includes(item));
                localStorage.setItem(localStorageManualOrderProp, [...extraItems, ...manualOrder].join(","));
                setManualOrder([...extraItems, ...manualOrder]);
            } else {
                // manualOrder has more strings, remove extras from grL
                localStorage.setItem(localStorageManualOrderProp, manualOrder.filter(item => grLStrArr.includes(item))?.join(","));
                setManualOrder(manualOrder.filter(item => grLStrArr.includes(item)));
            }
        }
    }, [grL]);

    useEffect(() => {
        if (compLvlState < 1 || !permissionLevelsMapState || grLState?.length == 0) return

        setCompLvlState(compLvl);
        setPermissionLevelsMapState(permissionLevelsMap);
        const hiddenState = grL.filter((item: ggroupcardprops) => {
            return (localStorage.getItem(localStorageHiddenGroupsProp) || "").indexOf(item.selectedGroup.selectionId) >= 0;
        });
        let mainState = grL.filter((item: ggroupcardprops) => {
            const hiddenFilter = (localStorage.getItem(localStorageHiddenGroupsProp) || "").indexOf(item.selectedGroup.selectionId) < 0;
            const mineFilter = !onlyMySpaces || item.selectedGroup.permissionlevel >= 3;
            const assistantTypeMatches = assistantTypeFilter === "all" || item.selectedGroup.assistanttype === assistantTypeFilter;

            return filteringProperty == "all"
                ? hiddenFilter && assistantTypeMatches
                : filteringProperty == "mine"
                ? hiddenFilter && mineFilter && assistantTypeMatches
                : filteringProperty == "hidden"
                ? !hiddenFilter && assistantTypeMatches
                : assistantTypeMatches;
        });

        if (sortingProperty === "manual") {
            if (manualOrder.length > 0) {
                mainState = [
                    ...mainState.filter(item => !manualOrder.includes(item.selectedGroup.selectionId)),
                    ...manualOrder.map(id => mainState.find(item => item.selectedGroup.selectionId === id)).filter(Boolean)
                ] as ggroupcardprops[];
            } else {
                manageManualOrder(mainState.map(item => item.selectedGroup.selectionId));
            }
        } else {
            mainState = mainState.slice().sort((a, b) => {
                const getValue = (item: ggroupcardprops) => {
                    switch (sortingProperty) {
                        case "dailymsgs":
                            return item.selectedGroup.dailymsgs;
                        case "filecount":
                            return item.selectedGroup.filecount;
                        case "oid":
                            return item.selectedGroup.oid;
                        default:
                            return 0;
                    }
                };
                const aValue = getValue(a);
                const bValue = getValue(b);
                if (aValue < bValue) return 1;
                if (aValue > bValue) return -1;
                return 0;
            });
        }
        if (mainState.length > 0) {
            setGrLMainState(mainState);
            setGrLHiddenState(hiddenState);
            setDisplayKey(displayKey + 1);
        }
    }, [refreshKey, grLState, onlyMySpaces, sortingProperty, filteringProperty, assistantTypeFilter]);

    const hasArrayChanged = (newArray: ggroupcardprops[]): boolean => {
        const newArrayString = JSON.stringify(newArray);
        let previousArrayString = JSON.stringify(grLState);
        const changed = previousArrayString !== newArrayString;
        previousArrayString = newArrayString;
        return changed;
    }

    const assistantTypeOptions: IDropdownOption[] = useMemo(() => {
        // get unique assistant types from the current grL array
        const uniqueTypes = new Set(grL.map(item => item.selectedGroup.assistanttype));

        // filter nmemap to only include types that appear in the current list
        const availableTypeOptions = Object.entries(nmeMap)
            .filter(([key]) => uniqueTypes.has(key))
            .map(([key, value]) => ({
                key: key,
                text: value
            }));

        return [
            { key: "all", text: "All Types" },
            ...availableTypeOptions
        ];
    }, [grL]);

    const onSortingChange = (_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        if (option) {
            localStorage.setItem("spaceSortingProperty", option.key as string);
            setSortingProperty(option.key as string);
        }
    };
    const onFilteringChange = (_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        if (option) {
            setFilteringProperty(option.key as string);
            if (option.key === "mine") {
                setOnlyMySpaces(true);
            } else {
                setOnlyMySpaces(false);
            }
        }
    };

    const onAssistantTypeChange = (_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        if (option) {
            localStorage.setItem(localStorageSpaceFilterProp, option.key as string);
            setAssistantTypeFilter(option.key as string);
        }
    };
    // DnD Handlers
    const manageManualOrder = (newOrder: string[]) => {
        setManualOrder(newOrder);
        localStorage.setItem(localStorageManualOrderProp, newOrder.join(","));
    };
    const handleDragStart = (event: any) => {
        const { active } = event;
        setActiveId(active.id);
        const draggedItem = grLMainState.find(item => item.selectedGroup.selectionId === active.id);
        setActiveItem(draggedItem || null);
    };
    const handleDragEnd = (event: any) => {
        const { active, over } = event;
        if (active.id !== over?.id && over) {
            const oldIndex = grLMainState.findIndex(item => item.selectedGroup.selectionId === active.id);
            const newIndex = grLMainState.findIndex(item => item.selectedGroup.selectionId === over.id);

            setGrLMainState(items => {
                const newItems = arrayMove(items, oldIndex, newIndex);
                // update manualOrder
                manageManualOrder(newItems.map(item => item.selectedGroup.selectionId));

                return newItems;
            });
        }
        setActiveId(null);
        setActiveItem(null);

        if (sortingProperty != "manual") {
            setSortingProperty("manual");
            localStorage.setItem(localStorageSpaceSortingProp, "manual");
        }
    };
    const handleDragCancel = () => {
        setActiveId(null);
        setActiveItem(null);
    };

    // Custom Hook for Sortable Items
    const SortableItem = (props: { item: ggroupcardprops; index: number }) => {
        const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
            id: props.item.selectedGroup.selectionId
        });

        const style = {
            transform: CSS.Transform.toString(transform),
            transition
        };
        const handleRefreshKey = () => {
            setRefreshKey(refreshKey + 1);
        };
        return (
            <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
                <DocumentCardComponent
                    index={props.index}
                    item={props.item}
                    newSpacePyro={newSpacePyro}
                    permissionLevelsMap={permissionLevelsMapState}
                    setRefreshKey={handleRefreshKey}
                    hidden={filteringProperty == "hidden"}
                    navChatLib={navChatLib}
                    rndr={true}
                />
            </div>
        );
    };

    return (
        <div key={displayKey} className={styles.carsdWrp}>

                <div className={styles.controls}>
                    <div className={styles.mySpacesOnlySwitchInCenter}>
                        <AnimAttention rectClassName={'thinAnim'}>
                            <Dropdown selectedKey={filteringProperty} options={filterOptions} onChange={onFilteringChange} styles={assitantCardsDropdownThinStyles} />
                        </AnimAttention>
                        <AnimAttention rectClassName={'thinAnim'}>
                            <Dropdown selectedKey={sortingProperty} options={sortingOptions} onChange={onSortingChange} styles={assitantCardsDropdownThinStyles} />
                        </AnimAttention>
                        <AnimAttention rectClassName={'wideAnim'}  alert={assistantTypeOptions.length > 1 && assistantTypeFilter != 'all' ? true : false }>
                            <Dropdown selectedKey={assistantTypeFilter} options={assistantTypeOptions} onChange={onAssistantTypeChange} styles={assitantCardsDropdownWiderStyles} />
                        </AnimAttention>
                    </div>
                </div>


            {grLState.length === 0 ? (
                <Stack
                    horizontal
                    wrap
                    tokens={{ childrenGap: 20, padding: 20, maxWidth: "97vw" }}
                    styles={{ root: { justifyContent: "left", maxWidth: "1000px" } }}
                    className={styles.gridStk}
                >
                    {Array.from({ length: 3 }).map((_, index) => (
                        <ShimmerDocumentCard key={index} />
                    ))}
                </Stack>
            ) : (
                <DndContext
                    sensors={isTouchDevice() ? mobileSensors : sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragCancel={handleDragCancel}
                >
                    <SortableContext items={grLMainState.map(item => item.selectedGroup.selectionId)} strategy={rectSortingStrategy}>
                        <Stack
                            wrap
                            horizontal
                            tokens={{ childrenGap: 20, padding: 20, maxWidth: "97vw" }}
                            styles={groupStackDnb}
                            className={styles.gridStk}
                        >
                            {compLvlState > 1 ? <CreateNewSpaceCard onNewGroupClick={onNewGroupClick} /> : null}

                            {grLMainState.map((item, index) => (
                                <SortableItem key={item.selectedGroup.selectionId} item={item} index={index} />
                            ))}
                        </Stack>
                    </SortableContext>
                </DndContext>
            )}
        </div>
    );
};

export default GroupCardsList;
